import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSiteState from '@app/state/SiteState';
import useSecurity from '@util/SecurityHook';
import Domain from '@app/model/_common/Domain';
import { BiScope, OptionsBasis } from '@app/model/bi/Bi';
import { useBiScope, usePropertyGroups } from '@app/model/bi/service/BiHooks';
import { CompanyHotelsContext } from '@app/site/secured/rateshopper/rates/provider/CompanyHotelsProvider';
import { resolveBiAddress, scopeValues, visibleGroups } from './Bi.utilities';

// ROO2-274: whether the user can open BI (the apps menu and the BI routes). The hotel open in the app gives BI at once when
// it has BI and the user may read it there. Otherwise BI opens when the BI scope has a property: only an admin or a user with
// a BI right on some hotel asks findBiScope (the BI pages reuse the answer); 'loading' until it answers. openHotel: the open
// hotel is in the BI scope, so BI can open on it.
export const useBiAccess = (): { state: 'yes' | 'no' | 'loading', openHotel: boolean } => {
	const security = useSecurity();
	const { selectedCompanyHotel } = useContext(CompanyHotelsContext);
	const openHotel = security.hasBiToolAccess(selectedCompanyHotel) && selectedCompanyHotel?.hasBiToolDomain() === true;
	const elsewhere = !openHotel && (security.isAdmin() || security.hasAnyCompanyHotelDomainEnabled(Domain.BI_TOOL));
	const scope = useBiScope(elsewhere);
	if (openHotel) return { state: 'yes', openHotel };
	if (!elsewhere) return { state: 'no', openHotel };
	// A failed refetch keeps the last scope (isError with data): BI stays open on it. A first failure opens BI too, so its
	// pages show their own error ("did not load") instead of "not found"; only an empty scope closes BI.
	if (scope.data !== undefined) return { state: scope.data.hotels.length > 0 ? 'yes' : 'no', openHotel };
	return { state: scope.isError ? 'yes' : 'loading', openHotel };
};

// The BI scope for the signed-in user: the hotels that findBiScope returns (enabled, BI domain BI_TOOL, BI access of the user),
// in the order and with the names of the account (CompanyHotelsContext = findMyCompanyHotelsLite). For all properties:
// the oldest import time, the first and the last stay date of the properties.
export const useAccountBiScope = () => {
	const scope = useBiScope();
	const { companyHotels } = useContext(CompanyHotelsContext);
	const data = useMemo<BiScope | undefined>(() => {
		if (scope.data === undefined) return undefined;
		const { hotels, ...rest } = scope.data;
		const byId = new Map(hotels.map(h => [h.companyHotelId, h]));
		const properties = companyHotels.flatMap(h => {
			const hotel = byId.get(h.id);
			return hotel === undefined ? [] : [{ ...hotel, name: h.mainHotelName, area: h.mainHotelLocation?.city ?? '' }];
		});
		return { ...rest, ...scopeValues(properties), properties };
	}, [scope.data, companyHotels]);
	// A failed refetch keeps the last scope (isError with data): the pages go on with it. Only a first failure is an error.
	return { data, isError: scope.isError && scope.data === undefined };
};

// The scope and the period of the BI pages from the address query (see resolveBiAddress), and `set`, which
// changes some values of the query and keeps the others. With no valid hotel in the query, the page shows the hotel that
// the app has open.
export const useBiAddress = (scope: BiScope | undefined) => {
	const [params, setParams] = useSearchParams();
	const { selectedCompanyHotel } = useContext(CompanyHotelsContext);
	const appHotelId = selectedCompanyHotel?.id ?? null;
	// The Property groups of the picker. A failed answer gives no group: the page works with the properties.
	const propertyGroups = usePropertyGroups();
	const groups = useMemo(() => visibleGroups(scope, propertyGroups.data?.groups ?? []), [scope, propertyGroups.data]);
	const address = useMemo(() => resolveBiAddress(scope, params, appHotelId, groups), [scope, params, appHotelId, groups]);
	// false until the groups are known, so that a link with ?group= asks for no report of All properties first.
	const ready = propertyGroups.data !== undefined || propertyGroups.isError;

	const set = useCallback((changes: Record<string, string>) => {
		setParams(current => {
			const next = new URLSearchParams(current);
			Object.entries(changes).forEach(([key, value]) => next.set(key, value));
			return next;
		}, { replace: true });
	}, [setParams]);
	// ROO2-310 B8: the Options switch, kept in the address as the revenue switches (?options=include).
	const setOptions = useCallback((options: OptionsBasis) => set({ options: options === 'INCLUDE' ? 'include' : 'exclude' }), [set]);

	return { ...address, set, includeOptions: address.options === 'INCLUDE', setOptions, search: params.toString(), groups, canEditGroups: propertyGroups.data?.canEdit ?? false, ready };
};

// When the user leaves the BI pages, the app opens the last hotel of the BI address (BI hotel picker decision 3), so the
// other products show the same hotel. "All properties" and an address with no hotel change nothing. The write waits for
// the exit: a change of the app hotel loads the hotel again (CompanyHotelsProvider renders nothing in the load), which
// would clear the BI page and its choices at each pick.
export const useBiHotelForApp = () => {
	const [params] = useSearchParams();
	const { companyHotels } = useContext(CompanyHotelsContext);
	const setSelectedCompanyHotelId = useSiteState(state => state.setSelectedCompanyHotelId);
	const last = useRef<number | null>(null);
	const hotel = params.get('hotel');

	useEffect(() => {
		last.current = companyHotels.find(h => String(h.id) === hotel)?.id ?? null;
	}, [companyHotels, hotel]);

	useEffect(() => () => {
		if (last.current !== null) setSelectedCompanyHotelId(last.current);
	}, [setSelectedCompanyHotelId]);
};
